import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import UserBadge from "@/components/UserBadge";
import { fetchFriends, friendSuggestions, requestFriend, acceptFriend, rejectFriend, unfriend } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { Users, UserPlus, Check, X, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function UserRow({ u, right }) {
  return (
    <li className="flex items-center gap-3 border border-white/10 rounded-md p-3 bg-obsidian-surface/60 hover:border-amber/40 transition" data-testid={`user-row-${u.user_id}`}>
      {u.picture ? (
        <img src={u.picture} alt="" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
      ) : (
        <div className="h-10 w-10 rounded-full bg-amber/20 text-amber flex items-center justify-center font-heading">{u.name?.[0] || "?"}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link to={`/u/${u.user_id}`} className="font-heading text-white hover:text-amber transition">{u.name || "User"}</Link>
          <UserBadge user={u} />
        </div>
        {u.common_titles ? <div className="text-xs text-zinc-500 font-body">{u.common_titles} common titles</div> : null}
      </div>
      {right}
    </li>
  );
}

export default function Friends() {
  const { user, loading: authLoading } = useAuth() || {};
  const [data, setData] = useState({ friends: [], sent: [], received: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [f, s] = await Promise.all([fetchFriends(), friendSuggestions().catch(() => [])]);
    setData(f); setSuggestions(s);
    setLoading(false);
  };
  useEffect(() => {
    if (authLoading || !user) return;
    load();
  }, [authLoading, user]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;
  if (!user) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Users className="h-10 w-10 text-amber mx-auto mb-4" />
          <h1 className="font-display text-4xl text-white">Friends</h1>
          <Button onClick={startGoogleLogin} className="mt-8 bg-amber hover:bg-amber-hover text-obsidian font-heading">Sign in</Button>
        </div>
      </div>
    );
  }

  const onAccept = async (id) => { await acceptFriend(id); toast.success("Friend added"); load(); };
  const onReject = async (id) => { await rejectFriend(id); toast.success("Declined"); load(); };
  const onRequest = async (other_id) => { await requestFriend(other_id); toast.success("Request sent"); load(); };
  const onUnfriend = async (other_id) => { await unfriend(other_id); toast.success("Removed"); load(); };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-14" data-testid="friends-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2">Your circle</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white mb-8">Friends</h1>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>
        ) : (
          <div className="space-y-10">
            {data.received.length > 0 && (
              <section data-testid="friends-received">
                <h2 className="font-heading text-sm text-zinc-400 uppercase tracking-[0.22em] mb-3">Requests received</h2>
                <ul className="space-y-2">
                  {data.received.map((u) => (
                    <UserRow key={u.user_id} u={u} right={
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => onAccept(u.request_id)} className="bg-amber hover:bg-amber-hover text-obsidian font-heading" data-testid={`accept-${u.user_id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onReject(u.request_id)} className="text-zinc-400 hover:text-red-400" data-testid={`reject-${u.user_id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    } />
                  ))}
                </ul>
              </section>
            )}

            <section data-testid="friends-list">
              <h2 className="font-heading text-sm text-zinc-400 uppercase tracking-[0.22em] mb-3">Friends ({data.friends.length})</h2>
              {data.friends.length === 0 ? (
                <p className="text-zinc-500 italic font-body">No friends yet — check suggestions below.</p>
              ) : (
                <ul className="space-y-2">
                  {data.friends.map((u) => (
                    <UserRow key={u.user_id} u={u} right={
                      <div className="flex gap-2">
                        <Link to={`/messages/${u.user_id}`} className="inline-flex items-center gap-1 text-xs text-amber hover:text-amber-hover font-heading">
                          <MessageSquare className="h-3.5 w-3.5" /> Message
                        </Link>
                        <button onClick={() => onUnfriend(u.user_id)} className="text-zinc-500 hover:text-red-400 text-xs font-heading" data-testid={`unfriend-${u.user_id}`}>Remove</button>
                      </div>
                    } />
                  ))}
                </ul>
              )}
            </section>

            {data.sent.length > 0 && (
              <section data-testid="friends-sent">
                <h2 className="font-heading text-sm text-zinc-400 uppercase tracking-[0.22em] mb-3">Pending requests sent</h2>
                <ul className="space-y-2">
                  {data.sent.map((u) => <UserRow key={u.user_id} u={u} right={<span className="text-xs text-zinc-500 font-heading">Pending</span>} />)}
                </ul>
              </section>
            )}

            <section data-testid="friend-suggestions">
              <h2 className="font-heading text-sm text-zinc-400 uppercase tracking-[0.22em] mb-3">Suggestions — same film taste</h2>
              {suggestions.length === 0 ? (
                <p className="text-zinc-500 italic font-body">Add films to your library to get taste-based suggestions.</p>
              ) : (
                <ul className="space-y-2">
                  {suggestions.map((u) => (
                    <UserRow key={u.user_id} u={u} right={
                      <Button size="sm" onClick={() => onRequest(u.user_id)} className="bg-amber hover:bg-amber-hover text-obsidian font-heading" data-testid={`request-${u.user_id}`}>
                        <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    } />
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
